import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { VideoPlayer } from '@/components/common/VideoPlayer'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'
import {
  useGenerationAttempt,
  useDeleteGenerationAttempt,
  type GenerationAttemptErrorLog,
} from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { LayoutTemplate, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

const STAGE_LABEL_MAP: Record<GenerationAttemptErrorLog['stage'], string> = {
  'frame-download': '프레임 이미지 다운로드',
  'video-generation': 'AI 생성',
  's3-upload': 'S3 업로드',
  'reference-copy': '참조 이미지 복사',
  'db-insert': 'DB 저장',
  'unexpected': '기타',
}

const FRAME_ROLE_LABEL: Record<string, string> = {
  first_frame: '첫 프레임',
  last_frame: '마지막 프레임',
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ko-KR')
}

export function FailedVideoDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: attempt, isLoading, error } = useGenerationAttempt(id!)
  const deleteMutation = useDeleteGenerationAttempt()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleDelete = async () => {
    if (!id) return

    try {
      await deleteMutation.mutateAsync(id)
      toast.success('실패 이력이 삭제되었습니다')
      navigate('/admin/failed-videos')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '삭제에 실패했습니다')
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!attempt) return null
  const videoThumbnailUrls = attempt.generatedVideo?.thumbnailUrls
    ?? (attempt.generatedVideo?.thumbnailUrl ? [attempt.generatedVideo.thumbnailUrl] : [])
  const primaryVideoThumbnailUrl = videoThumbnailUrls[0] ?? undefined

  return (
    <div className="max-w-5xl space-y-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/failed-videos">실패한 비디오</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">실패한 비디오 상세</h1>
          <p className="text-caption mt-2 leading-relaxed">
            실패한 생성 요청의 입력값과 에러 로그를 확인합니다
          </p>
        </div>
        <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
          <Trash2 className="w-4 h-4 mr-2" />
          삭제
        </Button>
      </div>

      <div className="bg-white rounded-lg border p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">시도 ID</label>
            <div className="mt-1 text-gray-900 font-mono text-sm">{attempt.id}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Generation ID</label>
            <div className="mt-1 text-gray-900 font-mono text-sm">{attempt.generationId}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">사용자</label>
            <div className="mt-1 text-gray-900">{attempt.user?.email ?? '-'}</div>
            <div className="text-xs text-gray-400 mt-1 font-mono">{attempt.userId}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">비율</label>
            <div className="mt-1 text-gray-900">{attempt.ratio}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">해상도</label>
            <div className="mt-1 text-gray-900">{attempt.resolution || '-'}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">길이</label>
            <div className="mt-1 text-gray-900">{attempt.duration ? `${attempt.duration}초` : '-'}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">카메라 고정</label>
            <div className="mt-1 text-gray-900">{attempt.cameraFixed ? '예' : '아니오'}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">모션 스타일</label>
            <div className="mt-1 text-gray-900">
              {attempt.motionStyle
                ? attempt.motionStyle === 'aggressive'
                  ? '역동적인 움직임'
                  : attempt.motionStyle === 'conservative'
                    ? '부드러운 움직임'
                    : attempt.motionStyle
                : '기본'}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">오디오 미포함</label>
            <div className="mt-1 text-gray-900">{attempt.noAudio ? '예' : '아니오'}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">생성일시</label>
            <div className="mt-1 text-gray-900 text-sm">{formatDateTime(attempt.createdAt)}</div>
          </div>

          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">템플릿</label>
            {attempt.template ? (
              <div className="mt-2 flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {attempt.template.thumbnailUrl ? (
                  <ImageWithFallback
                    src={attempt.template.thumbnailUrl}
                    alt={attempt.template.name}
                    className="w-16 h-16 rounded-lg object-cover border"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                    <LayoutTemplate className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <div className="font-medium text-gray-900">{attempt.template.name}</div>
                  {attempt.template.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{attempt.template.description}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-1 text-gray-400">사용된 템플릿 없음</div>
            )}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">프롬프트</label>
          <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
            {attempt.prompt}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">에러 요약</label>
          <div className="mt-1 p-4 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm">
            {attempt.errorSummary}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <label className="text-sm font-medium text-gray-700 block mb-4">
          에러 로그 ({attempt.errorLogs.length}개)
        </label>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-gray-50 border-y border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">단계</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">메시지</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">시도 정보</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">시각</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {attempt.errorLogs.map((log, idx) => (
                <tr key={`${log.loggedAt}-${idx}`}>
                  <td className="px-4 py-3">
                    <Badge variant="destructive" className="min-w-[3rem] justify-center whitespace-nowrap">
                      {STAGE_LABEL_MAP[log.stage]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800">{log.message}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                    {log.attempt ? `${log.attempt}회차` : '-'}
                    {log.maxRetries ? ` (최대 ${log.maxRetries})` : ''}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                    {formatDateTime(log.loggedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {attempt.frameImages && attempt.frameImages.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <label className="text-sm font-medium text-gray-700 block mb-4">
            프레임 이미지 ({attempt.frameImages.length}개)
          </label>
          <div className="grid grid-cols-4 gap-4">
            {attempt.frameImages.map((frame, idx) => (
              <div key={idx} className="bg-gray-100 rounded-lg overflow-hidden">
                <ImageWithFallback
                  src={frame.thumbnailUrl}
                  alt={`프레임 이미지 ${idx + 1}`}
                  className="w-full h-auto object-contain"
                  fallbackClassName="h-24"
                  loading="lazy"
                />
                <div className="text-xs text-gray-500 text-center py-1">
                  {FRAME_ROLE_LABEL[frame.role] || frame.role}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {attempt.generatedVideo && (
        <div className="bg-white rounded-lg border p-6">
          <label className="text-sm font-medium text-gray-700 block mb-4">
            생성된 비디오
          </label>
          <div className="max-w-2xl">
            <VideoPlayer
              src={attempt.generatedVideo.videoUrl}
              poster={primaryVideoThumbnailUrl}
              controls
              className="w-full rounded-lg border shadow-sm"
            />
            {attempt.generatedVideo.durationSeconds && (
              <div className="text-xs text-gray-500 font-mono text-center mt-2">
                {attempt.generatedVideo.durationSeconds}초
              </div>
            )}
            {videoThumbnailUrls.length > 0 && (
              <div className="mt-4">
                <div className="text-xs text-gray-500 mb-2">추출된 썸네일 ({videoThumbnailUrls.length}개)</div>
                <div className="grid grid-cols-4 gap-2">
                  {videoThumbnailUrls.map((url, index) => (
                    <ImageWithFallback
                      key={`${url}-${index}`}
                      src={url}
                      alt={`비디오 썸네일 ${index + 1}`}
                      className="w-full aspect-video object-cover rounded border border-gray-200"
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>실패 이력 삭제</DialogTitle>
            <DialogDescription className="mt-2">
              이 실패 이력을 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteMutation.isPending}
            >
              취소
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? '삭제 중...' : '삭제'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
